import { useFormik } from 'formik';
import _ from 'lodash/fp';
import React, { useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import { MEMBERSHIP } from '../../constants/membership';
import usePromiseWrapper from '../../hooks/usePromiseWrapper';
import { alertError, alertSuccess } from '../../redux/actions';
import { User } from '../../redux/rtk';
import text from '../../text';
import { client } from '../../utilities/api';
import assert from '../../utilities/assert';
import { captureError } from '../../utilities/error';
import {
  calculateRemainderDays,
  calculateTrialLengthDays,
} from '../../utilities/membership';
import { getUserMembership } from '../../utilities/user';
import { Button } from '../button';
import { Control } from '../form';

export const GIVE_USER_TRIAL_MODAL = 'GIVE_USER_TRIAL_MODAL';

interface GiveUserTrialModalProps {
  user: User;
  show: boolean;
  onHide: () => void;
  rest?: Omit<
    React.ComponentProps<typeof Modal>,
    'show' | 'onHide' | 'centered' | 'scrollable'
  >;
}

const GiveUserTrialModal = ({
  user,
  show,
  onHide,
  ...rest
}: GiveUserTrialModalProps) => {
  const dispatch = useDispatch();
  const formik = useFormik({
    initialValues: { days: '' },
    validate: ({ days }) => {
      const errors: { days?: string } = {};
      days = days.trim();
      if (days === '') {
        errors.days = 'Required';
      } else if (!/^\d+$/.test(days)) {
        errors.days = 'Please enter a valid number';
      } else {
        const asInt = parseInt(days, 10);
        if (isNaN(asInt)) {
          throw new Error(`Could not parseInt: ${days}`);
        }
        // coordinate with backend
        if (asInt < 1 || asInt > 100) {
          errors.days = 'Please enter a value between 1 and 100';
        }
      }
      return errors;
    },
    onSubmit: async ({ days }, { setSubmitting }) => {
      try {
        await addDaysToTrial(days);
        if (onHide) {
          onHide();
        }
        dispatch(alertSuccess(`${days} days Trial added to ${user.email}`));
      } catch (err) {
        captureError(err);
        dispatch(alertError('Failed to add days to trial'));
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { run: addDaysToTrial } = usePromiseWrapper(
    async (days: number) => {
      assert(!_.isNil(user?.id));
      const { data } = await client.giveUserTrial(user.id, {
        priceId: MEMBERSHIP.MONTHLY.PRICE_ID,
        paymentMethodId: undefined,
        days: Number(days),
      });
      return data;
    },
    [user?.id] // keep the ?: modal props can be undefined
  );
  const daysLeftOfTrial = useMemo(() => {
    const membership = getUserMembership(user);
    if (!membership || membership.status !== 'trialing') return null;
    return calculateRemainderDays(membership);
  }, [user]);
  const trialLengthInDays = useMemo(() => {
    const membership = getUserMembership(user);
    if (!membership || membership.status !== 'trialing') return null;
    return calculateTrialLengthDays(membership);
  }, [user]);
  return (
    <Modal centered scrollable show={show} onHide={onHide} {...rest}>
      {/* @ts-expect-error jsx type missing */}
      <Modal.Header className='align-end' closeButton>
        <div>
          <h3 className='secondary'>{text('addTrial')}</h3>
        </div>
      </Modal.Header>
      <p>
        {user
          ? !_.isNil(daysLeftOfTrial)
            ? `${user.email} has ${daysLeftOfTrial} days left of ${trialLengthInDays}-day trial.`
            : `${user.email} not yet on a trial.`
          : null}
      </p>
      <form onSubmit={formik.handleSubmit}>
        {/* @ts-expect-error jsx type missing */}
        <Control
          label={text('days')}
          as='input'
          type='text'
          name='days'
          isFailed={formik.touched.days ? !!formik.errors.days : null}
          value={formik.values.days}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          validationError={formik.touched.days ? formik.errors.days : null}
          autoComplete='off'
        />
        {/* @ts-expect-error jsx type missing */}
        <Button type='submit' className='mt-2' loading={formik.isSubmitting}>
          {text('addTrial')}
        </Button>
      </form>
    </Modal>
  );
};

export default GiveUserTrialModal;
